import { useState, useEffect } from "react";
import { useOutletContext, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Users,
  Search,
  Filter,
  Target,
  Flame,
  Clock,
  TrendingUp,
  TrendingDown,
  ChevronDown
} from "lucide-react";

export default function TeacherStudents() {
  const { teacher } = useOutletContext();
  const [searchParams] = useSearchParams();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedGrade, setSelectedGrade] = useState(searchParams.get("grade") || "");
  const [showFilters, setShowFilters] = useState(false);

  const assignedGrades = teacher?.assignments 
    ? Object.keys(teacher.assignments).filter(g => teacher.assignments[g]?.length > 0).sort((a, b) => parseInt(a) - parseInt(b))
    : [];

  useEffect(() => {
    fetchStudents();
  }, [selectedGrade]);

  const fetchStudents = async () => {
    setLoading(true);
    try {
      let url = `${API}/teacher/dashboard/students`;
      if (selectedGrade) {
        url += `?grade=${selectedGrade}`;
      }
      
      const response = await fetch(url, {
        credentials: "include"
      });
      
      if (response.ok) {
        const data = await response.json();
        setStudents(data.students || []);
      }
    } catch (error) {
      console.error("Failed to fetch students:", error);
    } finally {
      setLoading(false);
    }
  };

  // Filter students by search query
  const filteredStudents = students.filter(student => 
    student.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    student.student_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Group students by grade
  const studentsByGrade = filteredStudents.reduce((acc, student) => {
    const grade = student.grade || "Unknown";
    if (!acc[grade]) acc[grade] = [];
    acc[grade].push(student);
    return acc;
  }, {});

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 80) return "text-emerald-600 bg-emerald-50";
    if (accuracy >= 60) return "text-amber-600 bg-amber-50";
    return "text-red-600 bg-red-50";
  };

  const getAccuracyIcon = (accuracy) => {
    if (accuracy >= 80) return <TrendingUp className="w-4 h-4" />;
    if (accuracy >= 60) return <Target className="w-4 h-4" />;
    return <TrendingDown className="w-4 h-4" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Student Progress</h1>
          <p className="text-gray-500 mt-1">
            Monitor performance across your assigned classes
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="py-1.5">
            <Users className="w-4 h-4 mr-1" />
            {filteredStudents.length} students
          </Badge>
        </div>
      </div>

      {/* Search & Filters */}
      <Card className="border-0 shadow-md">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by name or student ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Grade Filter */}
            <div className="relative">
              <select
                value={selectedGrade}
                onChange={(e) => setSelectedGrade(e.target.value)}
                className="appearance-none w-full md:w-48 px-4 py-2 pr-10 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Grades</option>
                {assignedGrades.map((grade) => (
                  <option key={grade} value={grade}>Grade {grade}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Students List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="h-20 bg-gray-200 rounded-lg animate-pulse"></div>
          ))}
        </div>
      ) : filteredStudents.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(studentsByGrade)
            .sort(([a], [b]) => parseInt(a) - parseInt(b))
            .map(([grade, gradeStudents]) => (
              <Card key={grade} className="border-0 shadow-md overflow-hidden">
                <CardHeader className="bg-gray-50 py-3 px-4">
                  <CardTitle className="text-base flex items-center justify-between">
                    <span>Grade {grade}</span>
                    <Badge variant="secondary">{gradeStudents.length} students</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="divide-y divide-gray-100">
                    {gradeStudents.map((student) => (
                      <div 
                        key={student.id}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="font-medium text-blue-600">
                              {student.name?.charAt(0)?.toUpperCase() || "?"}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{student.name}</p>
                            <p className="text-sm text-gray-500">{student.student_id}</p>
                          </div>
                        </div>

                        <div className="flex items-center gap-6">
                          {/* Accuracy */}
                          <div className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${getAccuracyColor(student.accuracy)}`}>
                            {getAccuracyIcon(student.accuracy)}
                            {student.accuracy}%
                          </div>

                          {/* Questions */}
                          <div className="text-center hidden md:block">
                            <p className="text-sm font-medium text-gray-900">{student.total_questions}</p>
                            <p className="text-xs text-gray-500">questions</p>
                          </div>

                          {/* Streak */}
                          {student.streak > 0 && (
                            <div className="flex items-center gap-1 text-orange-500 hidden md:flex">
                              <Flame className="w-4 h-4" />
                              <span className="text-sm font-medium">{student.streak}</span>
                            </div>
                          )}

                          {/* Activity Status */}
                          <div className={`w-3 h-3 rounded-full ${student.is_active_today ? 'bg-emerald-500' : 'bg-gray-300'}`}
                            title={student.is_active_today ? 'Active today' : 'Not active today'}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      ) : (
        <Card className="border-0 shadow-md">
          <CardContent className="py-16 text-center">
            <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Students Found</h3>
            <p className="text-gray-500">
              {searchQuery 
                ? "No students match your search criteria."
                : "No students enrolled in your assigned classes yet."}
            </p>
          </CardContent>
        </Card>
      )}

      {/* Legend */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-sm text-gray-500 mb-2">Legend:</p>
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
              <span className="text-gray-600">Active today</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-gray-300"></div>
              <span className="text-gray-600">Not active</span>
            </div>
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-orange-500" />
              <span className="text-gray-600">Day streak</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs">80%+</span>
              <span className="text-gray-600">Excellent</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-amber-50 text-amber-600 rounded text-xs">60-79%</span>
              <span className="text-gray-600">Good</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 bg-red-50 text-red-600 rounded text-xs">&lt;60%</span>
              <span className="text-gray-600">Needs attention</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
